<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-4">
          <h3 class="text-h5">Gestión de Estados de DM</h3>
          <v-btn color="primary" @click="showChangeStateDialog = true" prepend-icon="mdi-state-machine">
            Cambiar Estado
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Filtros -->
    <v-row>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="busqueda"
          label="Buscar Dispositivo"
          prepend-inner-icon="mdi-magnify"
          clearable
          @update:model-value="fetchHistorialEstados"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          v-model="filtroEstado"
          :items="estadosDisponibles"
          label="Filtrar por Estado"
          clearable
          @update:model-value="fetchHistorialEstados"
        ></v-select>
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          v-model="filtroFecha"
          :items="opcionesFecha"
          label="Período"
          @update:model-value="fetchHistorialEstados"
        ></v-select>
      </v-col>
    </v-row>

    <!-- Tabla de Historial de Estados -->
    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="historialEstados"
          :loading="loading"
          class="elevation-1"
        >
          <template v-slot:item.fecha_cambio="{ item }">
            {{ formatDateTime(item.fecha_cambio) }}
          </template>

          <template v-slot:item.estado="{ item }">
            <v-chip :color="getEstadoColor(item.estado)" size="small">
              {{ item.estado }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              icon="mdi-eye"
              size="small"
              @click="verDetalle(item)"
              title="Ver Detalle"
            ></v-btn>
            <v-btn
              icon="mdi-history"
              size="small"
              color="info"
              @click="verHistorialCompleto(item)"
              title="Ver Historial Completo"
            ></v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- Diálogo: Cambiar Estado -->
    <v-dialog v-model="showChangeStateDialog" max-width="700" persistent>
      <v-card>
        <v-card-title>Cambiar Estado de Dispositivo Médico</v-card-title>
        <v-card-text>
          <v-form ref="changeStateForm">
            <v-row>
              <!-- Selección de Dispositivo -->
              <v-col cols="12">
                <v-autocomplete
                  v-model="cambioEstado.inventory_item_id"
                  :items="dispositivosDisponibles"
                  item-title="descripcion"
                  item-value="id"
                  label="Dispositivo Médico *"
                  :rules="[v => !!v || 'Requerido']"
                  required
                  @update:model-value="loadEstadoActual"
                >
                  <template v-slot:item="{ props, item }">
                    <v-list-item v-bind="props">
                      <v-list-item-subtitle>
                        Lote: {{ item.raw.lote }} | S/N: {{ item.raw.serial_number || 'N/A' }}
                      </v-list-item-subtitle>
                      <v-list-item-subtitle>
                        Estado Actual: <v-chip size="x-small" :color="getEstadoColor(item.raw.estado_actual)">
                          {{ item.raw.estado_actual || 'Sin Estado' }}
                        </v-chip>
                      </v-list-item-subtitle>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </v-col>

              <!-- Estado Actual -->
              <v-col cols="12" v-if="estadoActual">
                <v-alert type="info" density="compact">
                  <strong>Estado Actual:</strong> {{ estadoActual }}
                </v-alert>
              </v-col>

              <!-- Nuevo Estado -->
              <v-col cols="12" md="6">
                <v-select
                  v-model="cambioEstado.nuevo_estado"
                  :items="estadosDisponibles"
                  label="Nuevo Estado *"
                  :rules="[v => !!v || 'Requerido']"
                  required
                ></v-select>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="cambioEstado.fecha_cambio"
                  label="Fecha del Cambio *"
                  type="datetime-local"
                  :rules="[v => !!v || 'Requerido']"
                  required
                ></v-text-field>
              </v-col>

              <!-- Motivo según el estado -->
              <v-col cols="12" v-if="cambioEstado.nuevo_estado === 'Devuelto a proveedor'">
                <v-text-field
                  v-model="cambioEstado.motivo_devolucion"
                  label="Motivo de Devolución *"
                  :rules="[v => !!v || 'Requerido para devoluciones']"
                  required
                ></v-text-field>
              </v-col>

              <v-col cols="12" v-if="cambioEstado.nuevo_estado === 'Destruido/Eliminado'">
                <v-select
                  v-model="cambioEstado.metodo_destruccion"
                  :items="metodosDestruccion"
                  label="Método de Destrucción *"
                  :rules="[v => !!v || 'Requerido']"
                  required
                ></v-select>
              </v-col>

              <v-col cols="12" v-if="cambioEstado.nuevo_estado === 'Destruido/Eliminado'">
                <v-text-field
                  v-model="cambioEstado.acta_destruccion"
                  label="N° Acta de Destrucción *"
                  :rules="[v => !!v || 'Requerido']"
                  required
                  hint="Número del documento que certifica la destrucción"
                  persistent-hint
                ></v-text-field>
              </v-col>

              <v-col cols="12" v-if="cambioEstado.nuevo_estado === 'En cuarentena'">
                <v-text-field
                  v-model="cambioEstado.motivo_cuarentena"
                  label="Motivo de Cuarentena *"
                  :rules="[v => !!v || 'Requerido']"
                  required
                ></v-text-field>
              </v-col>

              <!-- Observaciones -->
              <v-col cols="12">
                <v-textarea
                  v-model="cambioEstado.observaciones"
                  label="Observaciones / Notas *"
                  rows="3"
                  :rules="[v => !!v || 'Las observaciones son requeridas']"
                  required
                  hint="Describa el motivo y contexto del cambio de estado"
                  persistent-hint
                ></v-textarea>
              </v-col>

              <!-- Alerta para estados críticos -->
              <v-col cols="12" v-if="['Destruido/Eliminado', 'Retirado'].includes(cambioEstado.nuevo_estado)">
                <v-alert type="warning" density="compact">
                  <strong>Atención:</strong> Este cambio de estado es irreversible. El dispositivo quedará fuera del inventario activo.
                </v-alert>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="cancelChangeState">Cancelar</v-btn>
          <v-btn 
            color="primary" 
            @click="confirmarCambioEstado" 
            :loading="submitting"
            :disabled="!cambioEstado.nuevo_estado"
          >
            Confirmar Cambio
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Historial Completo -->
    <v-dialog v-model="showHistorialDialog" max-width="900">
      <v-card>
        <v-card-title>Historial Completo de Estados</v-card-title>
        <v-card-subtitle v-if="selectedDispositivo">
          {{ selectedDispositivo.dispositivo_nombre }} - Lote: {{ selectedDispositivo.lote }}
        </v-card-subtitle>
        <v-card-text>
          <v-timeline side="end">
            <v-timeline-item
              v-for="(estado, index) in historialCompleto"
              :key="index"
              :dot-color="getEstadoColor(estado.estado)"
              size="small"
            >
              <template v-slot:opposite>
                <div class="text-caption">
                  {{ formatDateTime(estado.fecha_cambio) }}
                </div>
              </template>
              <v-card>
                <v-card-title class="text-h6">
                  <v-chip :color="getEstadoColor(estado.estado)" size="small">
                    {{ estado.estado }}
                  </v-chip>
                </v-card-title>
                <v-card-text>
                  <p><strong>Usuario:</strong> {{ estado.usuario_responsable }}</p>
                  <p v-if="estado.observaciones"><strong>Observaciones:</strong> {{ estado.observaciones }}</p>
                </v-card-text>
              </v-card>
            </v-timeline-item>
          </v-timeline>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showHistorialDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import apiClient from '@/api'

export default {
  name: 'EstadoDM',
  data() {
    return {
      loading: false,
      submitting: false,
      showChangeStateDialog: false,
      showHistorialDialog: false,
      busqueda: '',
      filtroEstado: null,
      filtroFecha: '30',
      historialEstados: [],
      historialCompleto: [],
      dispositivosDisponibles: [],
      selectedDispositivo: null,
      estadoActual: null,
      cambioEstado: {
        inventory_item_id: null,
        nuevo_estado: null,
        fecha_cambio: new Date().toISOString().slice(0, 16),
        observaciones: '',
        motivo_devolucion: '',
        motivo_cuarentena: '',
        metodo_destruccion: '',
        acta_destruccion: ''
      },
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      },
      headers: [
        { title: 'Dispositivo', key: 'dispositivo_nombre', sortable: true },
        { title: 'Lote', key: 'lote', sortable: true },
        { title: 'Estado', key: 'estado', sortable: true },
        { title: 'Fecha Cambio', key: 'fecha_cambio', sortable: true },
        { title: 'Usuario', key: 'usuario_responsable', sortable: true },
        { title: 'Acciones', key: 'actions', sortable: false }
      ],
      estadosDisponibles: [
        'En uso',
        'Disponible',
        'En cuarentena',
        'Devuelto a proveedor',
        'Destruido/Eliminado',
        'Retirado',
        'Reemplazado',
        'En mantenimiento',
        'Perdido'
      ],
      metodosDestruccion: [
        'Incineración',
        'Autoclave',
        'Disposición por empresa autorizada',
        'Otro'
      ],
      opcionesFecha: [
        { title: 'Últimos 7 días', value: '7' },
        { title: 'Últimos 30 días', value: '30' },
        { title: 'Últimos 90 días', value: '90' },
        { title: 'Todo', value: 'all' }
      ]
    }
  },
  mounted() {
    this.fetchHistorialEstados()
    this.fetchDispositivosDisponibles()
  },
  methods: {
    async fetchHistorialEstados() {
      this.loading = true
      try {
        const params = {}
        if (this.busqueda) params.busqueda = this.busqueda
        if (this.filtroEstado) params.estado = this.filtroEstado
        if (this.filtroFecha !== 'all') params.dias = this.filtroFecha

        const response = await apiClient.get('/dm/estados', { params })
        this.historialEstados = response.data || []
      } catch (error) {
        console.error('Error al cargar historial de estados:', error)
        this.showSnackbar('Error al cargar historial', 'error')
      } finally {
        this.loading = false
      }
    },

    async fetchDispositivosDisponibles() {
      try {
        const response = await apiClient.get('/dm/dispositivos-dm')
        this.dispositivosDisponibles = response.data.map(item => ({
          id: item.id,
          descripcion: `${item.product_name} - ${item.sku}`,
          lote: item.lot_number,
          serial_number: item.serial_number,
          estado_actual: item.estado_actual
        }))
      } catch (error) {
        console.error('Error al cargar dispositivos:', error)
      }
    },

    async loadEstadoActual(inventoryItemId) {
      if (!inventoryItemId) {
        this.estadoActual = null
        return
      }

      try {
        // Obtener el estado actual desde el historial de estados
        const response = await apiClient.get('/dm/estados', {
          params: { dias: 'all' }
        })
        
        // Buscar el último estado para este dispositivo
        const estadosDispositivo = (response.data || []).filter(
          estado => estado.inventory_item_id === inventoryItemId
        )
        
        if (estadosDispositivo.length > 0) {
          // Ordenar por fecha y tomar el más reciente
          estadosDispositivo.sort((a, b) => 
            new Date(b.fecha_cambio) - new Date(a.fecha_cambio)
          )
          this.estadoActual = estadosDispositivo[0].estado_nuevo || estadosDispositivo[0].estado || 'Sin Estado'
        } else {
          // Si no hay historial, buscar en dispositivos disponibles
          const dispositivo = this.dispositivosDisponibles.find(d => d.id === inventoryItemId)
          this.estadoActual = dispositivo?.estado_actual || 'Disponible'
        }
      } catch (error) {
        console.error('Error al cargar estado actual:', error)
        // Intentar obtener desde dispositivos disponibles como fallback
        const dispositivo = this.dispositivosDisponibles.find(d => d.id === inventoryItemId)
        this.estadoActual = dispositivo?.estado_actual || 'Desconocido'
      }
    },

    async confirmarCambioEstado() {
      if (!this.$refs.changeStateForm.validate()) return

      this.submitting = true
      try {
        const payload = {
          inventory_item_id: this.cambioEstado.inventory_item_id,
          tipo_evento: 'Cambio Estado',
          fecha_evento: this.cambioEstado.fecha_cambio,
          detalles: {
            estado_anterior: this.estadoActual || 'Sin Estado',
            estado_nuevo: this.cambioEstado.nuevo_estado,
            observaciones: this.cambioEstado.observaciones,
            motivo_devolucion: this.cambioEstado.motivo_devolucion,
            motivo_cuarentena: this.cambioEstado.motivo_cuarentena,
            metodo_destruccion: this.cambioEstado.metodo_destruccion,
            acta_destruccion: this.cambioEstado.acta_destruccion
          },
          notas: this.cambioEstado.observaciones
        }

        await apiClient.post('/dm/trazabilidad/evento', payload)
        
        this.showSnackbar('Estado cambiado exitosamente', 'success')
        this.showChangeStateDialog = false
        this.fetchHistorialEstados()
        this.$emit('refresh')
      } catch (error) {
        console.error('Error al cambiar estado:', error)
        this.showSnackbar('Error al cambiar estado', 'error')
      } finally {
        this.submitting = false
      }
    },

    async verHistorialCompleto(item) {
      this.selectedDispositivo = item
      
      try {
        const response = await apiClient.get(`/dm/trazabilidad/${item.inventory_item_id}`)
        const data = response.data?.data || response.data || {}
        
        // Obtener historial de estados y agregar campo 'estado'
        this.historialCompleto = (data.historial_estados || []).map(estado => ({
          ...estado,
          estado: estado.estado_nuevo || estado.estado,
          usuario_responsable: estado.usuario_responsable?.username || estado.usuario_responsable || 'Desconocido',
          observaciones: estado.notas || estado.observaciones || ''
        }))
        
        this.showHistorialDialog = true
      } catch (error) {
        console.error('Error al cargar historial completo:', error)
        this.showSnackbar('Error al cargar historial completo', 'error')
      }
    },

    verDetalle(item) {
      // TODO: Implementar vista de detalle
      console.log('Ver detalle:', item)
    },

    cancelChangeState() {
      this.showChangeStateDialog = false
      this.cambioEstado = {
        inventory_item_id: null,
        nuevo_estado: null,
        fecha_cambio: new Date().toISOString().slice(0, 16),
        observaciones: '',
        motivo_devolucion: '',
        motivo_cuarentena: '',
        metodo_destruccion: '',
        acta_destruccion: ''
      }
      this.estadoActual = null
    },

    formatDateTime(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleString('es-ES')
    },

    getEstadoColor(estado) {
      const colors = {
        'Disponible': 'success',
        'En uso': 'info',
        'En cuarentena': 'warning',
        'Devuelto a proveedor': 'grey',
        'Destruido/Eliminado': 'error',
        'Retirado': 'grey',
        'Reemplazado': 'orange',
        'En mantenimiento': 'blue',
        'Perdido': 'red'
      }
      return colors[estado] || 'grey'
    },

    showSnackbar(message, color = 'success') {
      this.snackbar = { show: true, message, color }
    }
  }
}
</script>

